// VideoControlsBar.tsx
import React, { useState, useEffect, useRef } from 'react';
import './video-controls-bar.scss';

interface VideoControlsBarProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isPaused: boolean;
  isFullscreen: boolean;
  setIsFullscreen: (val: boolean) => void;
}

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const VideoControlsBar: React.FC<VideoControlsBarProps> = ({ videoRef, isPaused, isFullscreen, setIsFullscreen }) => {
  const [showControls, setShowControls] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const lastVolumeRef = useRef(volume);
  const hideTimeout = useRef<number | null>(null);
  const [progressTooltip, setProgressTooltip] = useState<{ visible: boolean; value: number; x: number }>({ visible: false, value: 0, x: 0 });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const updateProgress = () => {
      setCurrentTime(video.currentTime);
      const newProgress = (video.currentTime / video.duration) * 100 || 0;
      setProgress(newProgress);
      setDuration(video.duration || 0);
      // Update CSS custom property for the progress bar styling
      const progressBar = document.querySelector('.progress-bar') as HTMLInputElement;
      if (progressBar) {
        progressBar.style.setProperty('--progress', `${newProgress}%`);
      }
    };
    video.addEventListener('timeupdate', updateProgress);
    video.addEventListener('loadedmetadata', updateProgress);
    return () => {
      video.removeEventListener('timeupdate', updateProgress);
      video.removeEventListener('loadedmetadata', updateProgress);
    };
  }, [videoRef]);

  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
      hideTimeout.current = window.setTimeout(() => setShowControls(false), 3000);
    };
    const video = videoRef.current;
    video?.addEventListener('mousemove', handleMouseMove);
    return () => {
      video?.removeEventListener('mousemove', handleMouseMove);
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
    };
  }, []);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const newTime = (parseFloat(e.target.value) / 100) * video.duration;
    video.currentTime = newTime;
    setProgress(parseFloat(e.target.value));
    // Update CSS custom property for the progress bar styling
    e.target.style.setProperty('--progress', `${e.target.value}%`);
  };

  const handleVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isMuted) {
      video.muted = false;
      setVolume(lastVolumeRef.current);
    } else {
      lastVolumeRef.current = volume;
      video.muted = true;
      setVolume(0);
    }
    setIsMuted(!isMuted);
  };

  const handleFullscreen = () => {
    if (isFullscreen) {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    } else {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    }
  };

  // Tooltip logic for progress bar
  const handleProgressMouseMove = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    const rect = (e.target as HTMLInputElement).getBoundingClientRect();
    const percent = (e.nativeEvent.offsetX / rect.width);
    const video = videoRef.current;
    let value = 0;
    if (video && video.duration) {
      value = percent * video.duration;
    }
    setProgressTooltip({ visible: true, value, x: e.nativeEvent.offsetX });
  };
  const handleProgressMouseLeave = () => {
    setProgressTooltip((prev) => ({ ...prev, visible: false }));
  };

  if (!(isPaused || showControls)) return null;
  return (
    <div className={`video-controls-bar visible`}
      style={{ opacity: 1, transition: 'opacity 0.4s', pointerEvents: 'auto' }}>
      <div className="progress-bar-container">
        <input
          type="range"
          min={0}
          max={100}
          value={progress}
          onChange={handleSeek}
          className="progress-bar"
          style={{ width: '95%' }}
          title="Seek"
          placeholder="Seek"
          onMouseMove={handleProgressMouseMove}
          onMouseLeave={handleProgressMouseLeave}
        />
        <span className="time-label">{formatTime(currentTime)}</span>
        {progressTooltip.visible && (
          <div
            className="progress-tooltip"
            style={{ left: `${progressTooltip.x}px` }}
          >
            {formatTime(progressTooltip.value)}
          </div>
        )}
      </div>
      <div className="controls-row">
        <div className="controls-left">
          {/* Play / pause on the same 24-unit grid as the skip and 15 s buttons, filling it the way their
              glyphs do: the old 32-unit icon used half its box and read smaller than its neighbours. */}
          <button className="control-btn" onClick={handlePlayPause} title={isPaused ? "Play" : "Pause"}>
            {isPaused ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M6.75 5.25v13.5a1.5 1.5 0 0 0 2.28 1.28l11.04-6.75a1.5 1.5 0 0 0 0-2.56L9.03 3.97a1.5 1.5 0 0 0-2.28 1.28"></path></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" viewBox="0 0 24 24"><rect width="4.5" height="15" x="5.25" y="4.5" fill="currentColor" rx="1"></rect><rect width="4.5" height="15" x="14.25" y="4.5" fill="currentColor" rx="1"></rect></svg>
            )}
          </button>
          <button className="control-btn" onClick={() => { if (videoRef.current) videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10); }} title="Rewind 10s">
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M16.498 4.704 6.75 10.35V6.375a.75.75 0 1 0-1.5 0v11.25a.75.75 0 0 0 1.5 0V13.65l9.748 5.646a1.5 1.5 0 0 0 2.252-1.298V6.002a1.5 1.5 0 0 0-2.252-1.298"></path></svg>
          </button>
          <button className="control-btn" onClick={() => { if (videoRef.current) videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10); }} title="Rewind 10s">
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M11.998 3.5a8.25 8.25 0 0 1 0 16.5.75.75 0 0 0 0 1.5c5.385 0 9.75-4.365 9.75-9.75S17.382 2 11.998 2s-9.75 4.365-9.75 9.75H.388a.187.187 0 0 0-.147.305l2.61 3.262a.188.188 0 0 0 .293 0l2.61-3.262a.187.187 0 0 0-.146-.305h-1.86a8.25 8.25 0 0 1 8.25-8.25"></path><path fill="currentColor" d="m7.498 9.616 1.556-.866h1.556v6.808H9.054v-5.262l-1.556.866zM16.498 13.418c0 1.498-1.167 2.276-2.48 2.276s-2.48-.778-2.48-2.276h1.556c0 .418.282.759.924.759.641 0 .924-.34.924-.759 0-.418-.283-.758-.924-.758h-2.179l.282-3.91h4.036v1.459H13.58l-.068.934h.603c1.313 0 2.383.778 2.383 2.275"></path></svg>
          </button>
          <button className="control-btn" onClick={() => { if (videoRef.current) videoRef.current.currentTime = Math.min(videoRef.current.duration, videoRef.current.currentTime + 10); }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M12 3.5A8.25 8.25 0 1 0 12 20a.75.75 0 0 1 0 1.5c-5.385 0-9.75-4.365-9.75-9.75S6.615 2 12 2s9.75 4.365 9.75 9.75h1.86c.157 0 .245.182.146.305l-2.61 3.262a.188.188 0 0 1-.292 0l-2.61-3.262a.187.187 0 0 1 .146-.305h1.86A8.25 8.25 0 0 0 12 3.5"></path><path fill="currentColor" d="m7.5 9.616 1.556-.866h1.556v6.808H9.056v-5.262l-1.556.866zM16.5 13.418c0 1.498-1.167 2.276-2.48 2.276s-2.48-.778-2.48-2.276h1.556c0 .418.282.759.924.759s.924-.34.924-.759c0-.418-.282-.758-.924-.758h-2.179l.283-3.91h4.036v1.459h-2.578l-.068.934h.603c1.313 0 2.383.778 2.383 2.275"></path></svg>
          </button>
          <button className="control-btn" onClick={() => { if (videoRef.current) videoRef.current.currentTime = Math.min(videoRef.current.duration, videoRef.current.currentTime + 10); }} title="Forward 10s">
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="m7.502 4.704 9.748 5.646V6.375a.75.75 0 0 1 1.5 0v11.25a.75.75 0 0 1-1.5 0v-3.974l-9.748 5.645a1.5 1.5 0 0 1-2.252-1.298V6.002a1.5 1.5 0 0 1 2.252-1.298"></path></svg>
          </button>
          <button className="control-btn" title="Add">
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2.25A9.75 9.75 0 1 0 21.75 12 9.76 9.76 0 0 0 12 2.25m0 18A8.25 8.25 0 1 1 20.25 12 8.26 8.26 0 0 1 12 20.25M16.5 12a.75.75 0 0 1-.75.75h-3v3a.75.75 0 1 1-1.5 0v-3h-3a.75.75 0 1 1 0-1.5h3v-3a.75.75 0 1 1 1.5 0v3h3a.75.75 0 0 1 .75.75"></path></svg>
          </button>
        </div>
        <div className="controls-right">
          <div className="volume-control">
            <button className="control-btn" onClick={handleMute}>
              {isMuted || volume === 0 ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="#fff" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="#fff" />
                </svg>
              )}
            </button>
            <div className="volume-slider-container">
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={handleVolume}
                className="volume-bar"
                title="Volume"
              />
            </div>
          </div>
          <button className="control-btn control-btn-cc" title="Closed Captions">CC</button>
          <button className="control-btn" title="Settings">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" fill="#fff" />
            </svg>
          </button>
          <button className="control-btn" title="Cast">
            <svg x="0px" y="0px" width="100%" height="100%" viewBox="0 0 24 24" fill="white"><g><path id="cast_caf_icon_arch0" className="cast_caf_state_d" d="M1,18 L1,21 L4,21 C4,19.3 2.66,18 1,18 L1,18 Z"></path><path id="cast_caf_icon_arch1" className="cast_caf_state_d" d="M1,14 L1,16 C3.76,16 6,18.2 6,21 L8,21 C8,17.13 4.87,14 1,14 L1,14 Z"></path><path id="cast_caf_icon_arch2" className="cast_caf_state_d" d="M1,10 L1,12 C5.97,12 10,16.0 10,21 L12,21 C12,14.92 7.07,10 1,10 L1,10 Z"></path><path id="cast_caf_icon_box" className="cast_caf_state_d" d="M21,3 L3,3 C1.9,3 1,3.9 1,5 L1,8 L3,8 L3,5 L21,5 L21,19 L14,19 L14,21 L21,21 C22.1,21 23,20.1 23,19 L23,5 C23,3.9 22.1,3 21,3 L21,3 Z"></path><path id="cast_caf_icon_boxfill" className="cast_caf_state_h" d="M5,7 L5,8.63 C8,8.6 13.37,14 13.37,17 L19,17 L19,7 Z"></path></g></svg>
          </button>
          <button className="control-btn" title="Picture in Picture">
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" viewBox="0 0 24 24"><path fill="currentColor" fillRule="evenodd" d="M3.75 4.5A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5zm16.5 8.25V6H3.75v12H12v-3.75a1.5 1.5 0 0 1 1.5-1.5z" clipRule="evenodd"></path></svg>
          </button>
          <button className="control-btn" onClick={handleFullscreen} title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
            {isFullscreen ? (

              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M8 8h4v2H10v2H8V8zm8 0h4v4h-2v-2h-2V8zm4 8v4h-4v-2h2v-2h2zm-8 4H8v-4h2v2h2v2z" fill="#fff" />
              </svg>
            ) : (
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M8 8h4v2H10v2H8V8zm8 0h4v4h-2v-2h-2V8zm4 8v4h-4v-2h2v-2h2zm-8 4H8v-4h2v2h2v2z" fill="#fff" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoControlsBar;
